import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "content-container" }
const _hoisted_2 = { class: "icon-column" }
const _hoisted_3 = ["src"]

import { useI18n } from "vue-i18n";

import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
} from "vue";
import { displayService } from "@/_services";
import { updateForecastData, updateForecastColors } from "@/eventBus";
import LocationIcon from "@/components/Icons/LocationIcon.vue";


import { defineComponent, defineExpose } from "vue";

const __default__ = defineComponent({
  name: "WeatherWidget",
});

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
  city: {
    type: String,
    default: "Tunis",
  },
  unit: {
    type: String,
    default: "Celsius",
  },
  fontFamily: {
    type: String,
    default: "Arial",
  },
  fontSize: {
    type: [Number, String],
    default: 40,
  },
  fontColor: {
    type: String,
    default: "#FFFFFF",
  },
  backgroundColor: {
    type: String,
    default: "#03A9F4",
  },
  opacity: {
    type: Number,
    default: 1,
  },
  height: {
    type: Number,
    default: 200,
  },
  width: {
    type: Number,
    default: 300,
  },
  borderRadius: {
    type: Number,
    default: 0,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
},
  emits: ["update:forecastData"],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n();

const props = __props;
const emit = __emit;

const getBackgroundColorWithOpacity = (bgColor: any) => {
  const opacityValue = props.opacity / 100;

  //bgcolor rgba/hsla=> return as it is
  if (bgColor.includes("rgba") || bgColor.includes("hsla")) {
    return bgColor;
  }

  //convert hex to rgba
  if (bgColor.startsWith("#")) {
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacityValue})`;
  }

  return bgColor;
};

function hexToRgba(hex: string, alpha: number) {
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

const backgroundStyle = computed(() => {
  // gradient
  if (props.config.bgConfig?.selectedOption == "gradient") {
    const gradient = props.config.bgConfig.gradient;
    const opacityValue = props.opacity / 100;

    // Apply global opacity to gradient colors
    const start = hexToRgba(
      gradient.startColor,
      (gradient.startColorOpacity || 1) * opacityValue
    );
    const end = hexToRgba(
      gradient.endColor,
      (gradient.endColorOpacity || 1) * opacityValue
    );

    //direction
    let linearDirection = "to right";
    let radialPosition = gradient.direction || "center";

    switch (gradient.direction) {
      case "left-right":
        linearDirection = "to right";
        break;
      case "right-left":
        linearDirection = "to left";
        break;
      case "top-bottom":
        linearDirection = "to bottom";
        break;
      case "bottom-top":
        linearDirection = "to top";
        break;
      case "top-left-bottom-right":
        linearDirection = "135deg";
        break;
      case "top-right-bottom-left":
        linearDirection = "225deg";
        break;
      case "bottom-left-top-right":
        linearDirection = "45deg";
        break;
      case "bottom-right-top-left":
        linearDirection = "315deg";
        break;
      default:
        linearDirection = "to right";
    }

    if (gradient.type === "linear") {
      return `linear-gradient(${linearDirection}, ${start}, ${end})`;
    } else {
      //radial
      return `radial-gradient(circle at ${radialPosition}, ${start}, ${end})`;
    }
  }

  //bgColor
  if (props.config.bgConfig?.selectedOption == "color") {
    return getBackgroundColorWithOpacity(props.config.bgConfig.fillColor);
  }

  //image
  if (props.config.bgConfig?.selectedOption == "image") {
    const imageConfig = props.config.bgConfig.image;

    if (imageConfig.src && imageConfig.src.length > 0) {
      const imageUrl = imageConfig.src[0].url ? imageConfig.src[0].url : imageConfig.src[0];
      console.log("imageUrl", imageUrl);
      console.log("Decoded imageUrl", decodeURIComponent(imageUrl));

      // Get background properties from config or use defaults
      const backgroundSize = imageConfig.backgroundSize || "cover";
      const backgroundPosition = imageConfig.backgroundPosition || "center";
      const backgroundRepeat = imageConfig.backgroundRepeat || "no-repeat";

      const backgroundString = `url(${imageUrl}) ${backgroundPosition} / ${backgroundSize} ${backgroundRepeat}`;
      console.log("Final background string:", backgroundString);
      return backgroundString;
    }

    // Fallback if no image is available
    return "transparent";
  }

  //og fill color
  return getBackgroundColorWithOpacity(props.backgroundColor);
});

//sizing
const widgetContainer = ref(null);
const containerWidth = ref(props.width);
const containerHeight = ref(props.height);

//if date/time shown based on container size
const showDateTime = computed(() => {
  return containerWidth.value > 150 && containerHeight.value > 120;
});

// size calcul
const tempSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 40%height and 20%width
  return Math.floor(Math.min(height * 0.4, width * 0.2));
});

const iconSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 90%height and 30%width
  return Math.floor(Math.min(height * 0.9, width * 0.3));
});

const cityNameSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 20%height and 10%width
  return Math.floor(Math.min(height * 0.2, width * 0.1));
});

const dateTimeSize = computed(() => {
  const height = props.height || 200;
  const width = props.width || 300;
  // 15%height and 8%width
  return Math.floor(Math.min(height * 0.15, width * 0.08));
});

const weatherStatus = computed(() => {
  if (!weatherData.value) return "--";

  return t(weatherData.value.current.condition.text);
});

const language = ref("en");
const getLanguage = () => {
  const lang = localStorage.getItem("Language") || "en";
  if (lang == "fr") {
    language.value = "fr";
    locale.value = "fr";
  } else if (lang == "ar") {
    language.value = "ar";
    locale.value = "ar";
  } else {
    language.value = "en";
    locale.value = "en";
  }
};

let intervalId: number | undefined;

function scheduleWeatherFetch() {
  if (intervalId) clearInterval(intervalId);

  // init
  fetchWeatherData();

  // fecth every 2 hours
  intervalId = setInterval(() => {
    fetchWeatherData();
  }, 2 * 60 * 60 * 1000);
}

onMounted(() => {
  getLanguage();

  scheduleWeatherFetch();
});

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});

const weatherData: any = ref(null);
const loading = ref(false);
const error = ref<any>(null);

const location = computed(() => {
  return weatherData.value?.location?.name || props.city;
});

const temperature = computed(() => {
  if (!weatherData.value) return "--";

  if (props.unit === "Celsius") {
    return Math.round(weatherData.value.current.temp_c);
  } else {
    return Math.round(weatherData.value.current.temp_f);
  }
});

const weatherIcon = computed(() => {
  if (!weatherData.value) return null;
  const condition = weatherData.value.current.condition.code;
  try {
    if (condition === 1000) {
      return require("@/assets/images/weather/sunny.png");
    } else if (condition == 1006) {
      return require("@/assets/images/weather/cloudy.png");
    } else if (condition == 1009 || condition == 1030 || condition == 1147) {
      return require("@/assets/images/weather/overcast.png");
    } else if (condition === 1003) {
      return require("@/assets/images/weather/partly_cloudy.png");
    } else if (
      condition == 1273 ||
      condition == 1252 ||
      condition == 1249 ||
      condition === 1246 ||
      condition === 1240 ||
      condition == 1201 ||
      condition == 1198 ||
      condition === 1195 ||
      condition === 1192 ||
      condition === 1189 ||
      condition === 1186 ||
      condition == 1183 ||
      condition == 1180 ||
      condition === 1153 ||
      condition === 1150 ||
      condition == 1063 ||
      condition === 1289
    ) {
      return require("@/assets/images/weather/rainy.png");
    } else if (
      condition == 1264 ||
      condition === 1261 ||
      condition == 1258 ||
      condition == 1255 ||
      condition === 1243 ||
      condition === 1225 ||
      condition === 1222 ||
      condition === 1219 ||
      condition === 1216 ||
      condition == 1213 ||
      condition == 1210 ||
      condition === 1066 ||
      condition === 1114 ||
      condition === 1117
    ) {
      return require("@/assets/images/weather/snowy.png");
    } else if (
      condition == 1237 ||
      condition == 1168 ||
      condition == 1069 ||
      condition == 1072 ||
      condition === 1279 ||
      condition === 1282
    ) {
      return require("@/assets/images/weather/ice.png");
    } else if (
      condition == 1087 ||
      condition == 1276 ||
      condition == 1279 ||
      condition == 1282
    ) {
      return require("@/assets/images/weather/thunder_storm.png");
    } else if (condition == 1204 || condition == 1207) {
      return require("@/assets/images/weather/windy.png");
    } else {
      return `https:${weatherData.value.current.condition.icon}`;
    }
  } catch (e) {
    return `https:${weatherData.value.current.condition.icon}`;
  }
});

const formattedDateTime = computed(() => {
  if (!weatherData.value?.location?.localtime) {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")} • ${now.getDate().toString().padStart(2, "0")}/${(
      now.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}`;
  }

  const dateTime = new Date(weatherData.value.location.localtime);
  const hours = dateTime.getHours().toString().padStart(2, "0");
  const minutes = dateTime.getMinutes().toString().padStart(2, "0");
  const day = dateTime.getDate().toString().padStart(2, "0");
  const month = (dateTime.getMonth() + 1).toString().padStart(2, "0");

  return `${hours}:${minutes} • ${day}/${month}`;
});

const forecastData: any = ref({});
const fetchWeatherData = async () => {
  try {
    loading.value = true;
    const res = await displayService.getWeatherData(
      props.city,
      language.value,
      3,
      "no"
    );
    weatherData.value = res.data;
    forecastData.value = res.data.forecast;
    loading.value = false;
    // update event bus with the forecast data
    updateForecastData(res.data.forecast);
    console.log(
      "WeatherWidget: Updated forecast data in event bus:",
      res.data.forecast
    );
  } catch (err) {
    console.error("Error fetching weather data:", err);
    loading.value = false;
  }
};

watch(
  () => props.city,
  () => {
    fetchWeatherData();
  }
);

watch(
  () => forecastData.value,
  (newVal) => {
    if (newVal && newVal.forecastday) {
      emit("update:forecastData", newVal.forecastday);
    }
  },
  { deep: true }
);

watch(
  () => [props.backgroundColor, props.fontColor],
  ([newBgColor, newFontColor]) => {
    updateForecastColors({
      backgroundColor: newBgColor,
      fontColor: newFontColor,
      opacity: props.opacity,
    });
  },
  { immediate: true }
);

__expose({
  forecastData,
  loading,
});

// watch(
//   () => locale.value,
//   (newLocale) => {
//     console.log('Locale changed to:', newLocale);
//     fetchWeatherData();
//   }
// );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "weather-widget",
    style: _normalizeStyle({
      background: backgroundStyle.value,
      color: __props.fontColor,
      fontFamily: __props.fontFamily,
      borderRadius:
        typeof __props.borderRadius === 'number' ? __props.borderRadius + 'px' : __props.borderRadius,
      padding: `${Math.floor(
        Math.min(props.height * 0.1, props.width * 0.05)
      )}px`,
    }),
    ref_key: "widgetContainer",
    ref: widgetContainer
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        class: "info-column",
        style: _normalizeStyle({
          gap: `${Math.floor(
            Math.min(props.height * 0.05, props.width * 0.02)
          )}px`,
        })
      }, [
        _createElementVNode("div", {
          class: "city-name",
          style: _normalizeStyle({ fontSize: `${cityNameSize.value}px` })
        }, [
          _createVNode(LocationIcon, {
            width: cityNameSize.value,
            height: cityNameSize.value,
            color: __props.fontColor,
            strokeWidth: 1.5
          }, null, 8, ["width", "height", "color"]),
          _createElementVNode("span", null, _toDisplayString(location.value), 1)
        ], 4),
        _createElementVNode("div", {
          class: "temperature",
          style: _normalizeStyle({ fontSize: `${tempSize.value}px` })
        }, [
          _createTextVNode(_toDisplayString(temperature.value), 1),
          _cache[0] || (_cache[0] = _createElementVNode("span", { class: "degree" }, "°", -1))
        ], 4)
      ], 4),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", {
          class: "weather-icon",
          style: _normalizeStyle({
            width: `${iconSize.value}px`,
            height: `${iconSize.value}px`,
          })
        }, [
          (weatherIcon.value)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                src: weatherIcon.value,
                alt: "Weather condition",
                style: {"width":"100%","height":"100%"},
                class: "weather-icon-img"
              }, null, 8, _hoisted_3))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "icon-placeholder",
                style: _normalizeStyle({ width: `${iconSize.value}px`, height: `${iconSize.value}px` })
              }, null, 4))
        ], 4)
      ])
    ])
  ], 4))
}
}

})